<!--
  Created 10.10.2023
  Price component for the proposal show page.
-->
<template>
  <section class="relative bg-aqua-800 print:break-before-page print:!bg-transparent">
    <!-- Adjustment icon/box -->
    <SectionOptions
      v-if="contentEditable"
      class="z-20 text-white"
      section="prices" />
    <div class="container relative isolate mx-auto px-6 py-12 pb-16 md:py-24 md:pb-28 print:!py-8">
      <!-- Price proposal box -->
      <div
        v-if="contentEditable || proposal.data.sections.prices.visible"
        class="relative flex flex-col rounded bg-aqua-50 p-6 md:p-12">
        <!-- disabled cursor overlay if section is hidden -->
        <div
          class="z-10 hidden"
          :class="{ 'absolute left-0 top-0 !block size-full cursor-not-allowed' : !proposal.data.sections.prices.visible }" />
        <!-- Price proposal content -->
        <div
          class="mb-8 flex flex-col justify-between md:flex-row"
          :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
          <TextEditableTextarea
            v-if="contentEditable"
            id="section-prices-heading"
            v-model="proposal.data.sections.prices.heading"
            background-style="gray"
            class="lg:mr-2 xl:mr-8 2xl:mr-24"
            :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight '
              + 'text-2xl font-medium text-gray-800 '
              + 'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
            :placeholder="'Fill in prices heading..'"
            :show-buttons="false"
            :saved-changes="savedChanges"
            @edited="store.unsavedChanges.changes.proposal = true;" />
          <h1
            v-else
            class="text-2xl">
            {{ proposal.data?.sections?.prices?.heading ?? 'Pristilbud' }}
          </h1>
          <a class="items-center space-x-2 font-medium hover:cursor-pointer hover:underline max-md:-mb-4 max-md:mt-4 md:flex md:justify-center">
            <TextEditableTextarea
              v-if="contentEditable"
              id="section-prices-cta"
              v-model="proposal.data.sections.prices.cta"
              class="relative"
              background-style="gray"
              tab-rows="1"
              :auto-grow="false"
              text-area-max-length="30"
              editing-indicator-class="!ml-[1px]"
              :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight '
                + 'inline-block max-md:underline text-right '
                + 'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :placeholder="'Fill in prices call to action..'"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <p
              v-else
              class="inline-block max-md:underline print:hidden"
              @click="showProposalAcceptanceModal = true">{{ proposal.data.sections?.prices?.cta ?? 'Gi aksept på tilbudet her' }}</p>
            <ShoppingBagIcon class="inline-block size-5 print:hidden" />
          </a>
        </div>
        <!-- Price summary -->
        <div
          v-if="contentEditable || proposal.data.sections.prices.visible"
          class="grid gap-4 lg:grid-cols-2 lg:gap-8"
          :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
          <div class="rounded bg-white p-4 md:p-8">
            <TextEditableRichText
              v-if="contentEditable"
              id="priceOffer-column_one"
              v-model="priceOffer.column_one"
              :tab-rows="3"
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <TextEditableRichText
              v-else
              id="priceOffer-column_one"
              v-model="priceOffer.column_one"
              disabled
              :tab-rows="3"
              placeholder=""
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false" />
          </div>
          <div class="rounded bg-white p-4 md:p-8">
            <TextEditableRichText
              v-if="contentEditable"
              id="priceOffer-column_one"
              v-model="priceOffer.column_two"
              :tab-rows="3"
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <TextEditableRichText
              v-else
              id="priceOffer-column_two"
              v-model="priceOffer.column_two"
              disabled
              :tab-rows="3"
              placeholder=""
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false" />
          </div>
        </div>
      </div>
      <!-- agent information -->
      <div
        class="mt-4 grid divide-y rounded bg-white max-lg:text-sm lg:mt-8 lg:grid-cols-2 lg:divide-x lg:divide-y-0 print:!divide-y-0 print:bg-gray-50"
        :class="{ 'lg:!grid-cols-1' : !proposal.data.sections.prices.visible && !contentEditable }">
        <div
          v-if="contentEditable || proposal.data.sections.prices.visible"
          class="flex flex-row items-center justify-center gap-8 p-6 lg:gap-12 lg:p-12"
          :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
          <Avatar
            :src="proposal.project.manager?.profile_photo_url ?? proposal.creator.profile_photo_url"
            :alt="proposal.project.manager?.name ?? proposal.creator.name"
            size="6xl" />
          <div class="space-y-1">
            Med vennlig hilsen<br>
            <strong>{{ proposal.project.manager?.name ?? proposal.creator.name }}</strong><br>
            <span class="mt-4 inline-block">
              <a
                v-if="proposal.project.manager?.phone || proposal.creator.phone"
                :href="'tel:' + (proposal.project.manager?.phone ?? proposal.creator.phone).replace(' ', '')"
                class="block cursor-pointer underline print:no-underline">{{ proposal.project.manager?.phone ?? proposal.creator.phone }}</a>
              <a
                v-if="proposal.project.manager?.email || proposal.creator.email"
                :href="'mailto:' + proposal.project.manager?.email ?? proposal.creator.email"
                class="block cursor-pointer underline print:no-underline">{{ proposal.project.manager?.email ?? proposal.creator.email }}</a>
            </span>
          </div>
        </div>
        <div class="flex flex-col items-center justify-center space-y-1 p-6 lg:p-12">
          Tilbudet levert av:<br>
          <strong>{{ proposal.project.team.name }}</strong> <span v-if="proposal.project.team.org_no">(org.nr: {{ proposal.project.team.org_no }})</span><br>
          <span
            v-if="contentEditable || proposal.data.sections.prices.visible"
            :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
            <a
              :href="proposal.terms?.original_url ?? ''"
              target="_blank"
              class="block cursor-pointer text-center underline print:hidden">Se vilkår for turen</a>
            <a class="block cursor-pointer text-center underline print:hidden">Personvern og Cookies</a>
          </span>
        </div>
      </div>
      <div
        class="mt-6 flex flex-col justify-around max-md:space-y-4 md:mt-12 md:flex-row"
        :class="[
          { 'cursor-not-allowed opacity-50' : contentEditable && !proposal.data.sections.prices.visible }
        ]">
        <button
          v-if="!contentEditable"
          class="rounded border-2 border-transparent bg-white px-4 py-2 text-center font-medium text-gray-700 shadow-lg
            hover:border-gray-700 hover:text-gray-900 print:hidden"
          @click="openPrintDialogue">
          <div class="flex items-center justify-center space-x-2">
            <span class="inline-block">Tilbudet som PDF</span>
            <DocumentTextIcon class="inline-block size-5" />
          </div>
        </button>
        <button
          class="rounded border-2 border-transparent bg-white px-4 py-2 text-center font-medium text-gray-700 shadow-lg
            hover:border-gray-700 hover:text-gray-900 print:hidden"
          :class="[
            { 'hidden': !contentEditable && !proposal.data.sections.prices.visible },
            { 'pointer-events-none' : !proposal.data.sections.prices.visible }
          ]"
          @click="showProposalAcceptanceModal = true">
          <div class="flex items-center justify-center space-x-2">
            <span class="inline-block">{{ proposal.data.sections?.prices?.cta ?? 'Gi aksept på tilbudet her' }}</span>
            <ShoppingBagIcon class="inline-block size-5" />
          </div>
        </button>
      </div>
    </div>
    <!-- Offer acceptance modal -->
    <SimpleModal
      z-index-class="z-[60]"
      :box-class="[
        'w-11/12 md:w-4/5 lg:w-10/12 max-w-[900px] max-h-[80vh] relative z-50',
        contentEditable ? 'overflow-hidden' : 'overflow-y-auto'
      ]"
      :show="showProposalAcceptanceModal"
      @update:show="(value) => showProposalAcceptanceModal = value">
      <template #content>
        <!-- Block acceptance module for editing, and give notice of missing terms -->
        <div
          v-if="contentEditable"
          class="absolute z-10 flex size-full flex-col items-center justify-center space-y-8 rounded-lg bg-gray-300/90 p-12">
          <div
            v-if="!proposal.terms"
            class="w-8/12">
            <h2 class="font-bold text-red-700">
              Warning!
            </h2>
            <p class="font-medium text-red-700">
              It is not possible to accept the proposal version as terms are not uploaded.
              Please go to Team settings and upload terms to the brand you have selected for the proposal.
            </p>
          </div>
          <!-- If in create proposal we display notice accepting only works in proposal versions -->
          <div class="w-8/12">
            <h2 class="font-bold text-yellow-800">
              Note!
            </h2>
            <p class="font-medium text-yellow-800">
              It is not possible to accept the proposal in create proposal.<br>
              Please open one of the proposal versions and accept the proposal there.
            </p>
          </div>
          <div class="w-8/12">
            <button
              class="rounded border-2 border-transparent bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg hover:border-gray-700 hover:text-gray-900"
              @click="showProposalAcceptanceModal = false">
              Close
            </button>
          </div>
        </div>
        <!-- If proposal version accepted -->
        <div
          v-if="proposal.accepted && !contentEditable"
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            Proposal has been accepted!
          </h2>
        </div>
        <!-- If proposal version waiting for verification -->
        <div
          v-else-if="!retry && proposal.verification_email_sent && !contentEditable"
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            Proposal has been accepted!
          </h2>
          <p>Waiting for e-mail verification.</p>

          <p class="mt-5 text-sm">
            Didn't receive the e-mail? <a
              href="#"
              class="text-indigo-500 hover:text-indigo-800"
              @click="retry = true">Try filling out the form again</a>.
          </p>
        </div>
        <!-- If form was successfully sent -->
        <div
          v-else-if="!retry && formSent"
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            Thank you!
          </h2>
          <p>An email has been sent with an verification link.</p>

          <p class="mt-5 text-sm">
            Didn't receive the e-mail? <a
              href="#"
              class="text-indigo-500 hover:text-indigo-800"
              @click="retry = true">Try filling out the form again</a>.
          </p>
        </div>
        <!-- If not accepted -->
        <div
          v-else
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            Accept proposal
          </h2>
          <p class="mb-8 text-sm">
            Please review these details before accepting the proposal.
            <br>
            When accepting, you will receive an e-mail with a link to verify it is you who is accepting the proposal.
          </p>
          <form
            @submit.prevent="form.post(route('proposal.accept', { hash: proposal.hash }), {
              preserveScroll: true,
              preserveState: true,
              onSuccess: () => hideForm()
            })">
            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> You </label>
              <div class="col-span-2">
                <p> {{ proposal.client.name }} </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> Proposal </label>
              <div class="col-span-2">
                <p v-if="contentEditable">
                  {{ proposal.title }}
                </p>
                <p v-else>
                  {{ proposal.title }} (v{{ proposalVersions?.find(v => v.id === proposal.proposalVersionId)?.proposalVersionNumber ?? 0 }})
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> Travel date (from-to) </label>
              <div class="col-span-2">
                {{ travelDate }}
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> Number of travelers </label>
              <div class="col-span-2">
                <p> {{ proposal.pax }} </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> Amount per pax </label>
              <div class="col-span-2">
                <p> {{ proposal.amount_pr_pax }} </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
              <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Your full name </label>
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  id="name"
                  v-model="form.name"
                  name="name"
                  type="text"
                  required="required"
                  class="block min-w-[300px] max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder=""
                  @change="form.clearErrors('name')">
                <p
                  v-if="form.errors.name"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.name }}
                </p>
              </div>

              <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Your e-mail </label>
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  id="email"
                  v-model="form.email"
                  name="email"
                  type="email"
                  required="required"
                  class="block min-w-[300px] max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder=""
                  @change="form.clearErrors('email')">
                <p
                  v-if="form.errors.email"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.email }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Terms and conditions </label>
              <div class="col-span-2 flex flex-row items-center space-x-2">
                <input
                  id="accept-terms"
                  v-model="form.accept_terms"
                  :disabled="!proposal.terms"
                  :title="!proposal.terms ? 'Terms and conditions unavailable, please give notice to your tour operator' : 'Accept terms and conditions'"
                  name="accept-terms"
                  type="checkbox"
                  class="block size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  :class="{'cursor-not-allowed opacity-50': !proposal.terms && !contentEditable}">
                <label
                  for="accept-terms"
                  :title="!proposal.terms ? 'Terms and conditions unavailable, please give notice to your tour operator' : 'Accept terms and conditions'"
                  class="block text-sm font-medium leading-6 text-gray-900"
                  :class="{'cursor-not-allowed opacity-50': !proposal.terms && !contentEditable}">
                  I accept the <a
                    :href="proposal.terms?.original_url"
                    target="_blank"
                    class="inline-block cursor-pointer text-center underline">terms and conditions</a>.
                </label>
                <p
                  v-if="form.errors.accept_terms"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.accept_terms }}
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="flex justify-end">
              <CreateButton
                class="mr-2"
                type="submit"
                :disabled="form.processing || !form.accept_terms"
                :title="!form.accept_terms ? 'You cannot accept the proposal without accepting the terms and conditions' : 'Submit form'"
                :class="{'cursor-not-allowed opacity-50': form.processing || !form.accept_terms}">
                Accept proposal
              </CreateButton>
              <CancelButton
                class="ml-2"
                @click="close" />
            </div>
          </form>
        </div>
      </template>
    </SimpleModal>
    <!-- Print module -->
    <SimpleModal
      z-index-class="z-[60]"
      :show="showPrintDialogue"
      :box-class="[
        'w-11/12 md:w-4/5 lg:w-10/12 max-w-[900px] max-h-[80vh] relative z-50'
      ]"
      :outside-click="true"
      @update:show="(value) => { showPrintDialogue = value; systemStore.setPrintMode(value); }">
      <template #content>
        <header class="border-b border-gray-200 bg-gray-100 p-6">
          Skriv ut tilbudet / Lagre som PDF
        </header>
        <main class="flex flex-col">
          <div class="flex flex-col space-y-4 overflow-y-scroll p-8">
            Dersom du ønsker å skrive ut tilbudet, anbefaler vi at du følger disse stegene:
            <ol class="ml-4 mt-2 list-inside list-decimal">
              <li>
                Trykk på "Skriv ut tilbudet" knappen
              </li>
              <li> Velg "Lagre som PDF" som skriver dersom du ønsker tilbudet som PDF</li>
              <li>
                Trykk på "Flere innstillinger" og velg følgende:
                <ul class="ml-4 list-inside list-disc">
                  <li>Det anbefales å ha dokumentet i portrettmodus</li>
                  <li>Sett papirstørrelse til A4</li>
                  <li>Sett skalering til 100</li>
                  <li>Sett marger til ingen</li>
                  <li>Sett på "Skriv ut bakgrunner"</li>
                </ul>
              </li>
            </ol>
          </div>
          <div class="flex">
            <button
              class="mb-8 ml-8 block w-fit rounded border border-purple-800 bg-purple-700 px-4 py-2 text-sm font-medium text-white shadow-sm
                hover:border-purple-900 hover:bg-purple-800 focus:outline-none
                focus:ring-2 focus:ring-purple-800 focus:ring-offset-2 print:hidden"
              @click="printPage">
              Skriv ut tilbudet
            </button>
            <button
              class="mb-8 ml-8 block w-fit rounded border border-gray-400 bg-gray-300 px-4 py-2 text-sm font-medium text-gray-600 shadow-sm
                hover:border-gray-500 hover:bg-gray-400 hover:text-gray-700 focus:outline-none
                focus:ring-2 focus:ring-purple-800 focus:ring-offset-2 print:hidden"
              @click="closePrintDialogue">
              Lukk vindu
            </button>
          </div>
        </main>
      </template>
    </SimpleModal>
  </section>
</template>
<script setup>
/* external */
import {
  ShoppingBagIcon, DocumentTextIcon,
} from '@heroicons/vue/24/outline';
import Avatar from '@/Components/Avatar.vue';
import { useProposalStore } from '@/Stores/Proposal';
import { useSystemStore } from '@/Stores/System';
import {
  computed, ref, watch,
} from 'vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import TextEditableRichText from '@/Components/Forms/TextEditableRichText.vue';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import { useForm } from '@inertiajs/vue3';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton.vue';
import CancelButton from '@/ComponentsDeprecated/Buttons/CancelButton.vue';
import DateRangeFormatter from '@/Components/DateRangeFormatter.vue';
import Button from '@/Jetstream/Button.vue';
import SectionOptions from '@/Pages/Proposal/Components/SectionOptions.vue';
import moment from 'moment-timezone';
import { isSameMonth } from '@/utils';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const props = defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
  proposalVersions: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const systemStore = useSystemStore();
const store = useProposalStore();
const proposal = computed(() => store.proposal);
const priceOffer = computed(() => proposal.value.data.price_offer);
const savedChanges = computed(() => !store.unsavedChanges.changes.proposal);

const showProposalAcceptanceModal = ref(false);
const showPrintDialogue = ref(false);

const form = useForm({
  name: '',
  email: '',
  accept_terms: false,
});

const formSent = ref(false);
const retry = ref(false);

/**
 * Travel date formatted
 */
const travelDate = computed(() => {
  let dateFormatted = '';
  if (isSameMonth(proposal.value.start_date, proposal.value.end_date)) {
    dateFormatted = moment(proposal.value.start_date).format('D-')
      + moment(proposal.value.end_date).format('LL');
  } else {
    dateFormatted = `${moment(proposal.value.start_date).format('D. MMM')
    } - ${moment(proposal.value.end_date).format('D. MMM YYYY')}`;
  }

  return dateFormatted;
});

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * Close the offer acceptance modal
 */
function close() {
  form.reset();
  showProposalAcceptanceModal.value = false;
}

/**
 * Hide form after it is sent
 */
function hideForm() {
  formSent.value = true;
  retry.value = false;
}

/**
 * Open print dialogue
 */
function openPrintDialogue() {
  showPrintDialogue.value = true;
  systemStore.setPrintMode(true);

  // open all itinerary days
  if (store.itinerary.length > 0) {
    store.itinerary.forEach((day) => {
      day.active = true;
    });
  }
}

/**
 *
 */
function closePrintDialogue() {
  showPrintDialogue.value = false;
  systemStore.setPrintMode(false);

  // close all itinerary days except first day
  if (store.itinerary.length > 0) {
    store.itinerary.forEach((day, index) => {
      day.active = index === 0;
    });
  }
}

/**
 * Print the page
 */
function printPage() {
  window.print();
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

watch(() => form.client, () => {
  form.clearErrors('client_id');
});
watch(() => form.project, () => {
  form.clearErrors('project_id');
});
watch(() => form.range, () => {
  form.clearErrors('range');
});

</script>
